import React from "react";
import { Link } from "react-router-dom";
import { Instagram, Mail, Facebook, Phone, MapPin, Clock } from "lucide-react";
import { Input } from "./ui/input";
import { Button } from "./ui/button";
import axios from "axios";
import { toast } from "sonner";
import { openCookiePreferences } from "./CookieBanner";

export default function Footer() {
  const [email, setEmail] = React.useState("");
  const [gdprOk, setGdprOk] = React.useState(false);
  const [busy, setBusy] = React.useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (!gdprOk) { toast.error("Acepta la política de privacidad para suscribirte"); return; }
    setBusy(true);
    try {
      await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/newsletter/subscribe`, { email, consent: true });
      toast.success("¡Gracias por suscribirte!");
      setEmail(""); setGdprOk(false);
    } catch { toast.error("Error, inténtalo de nuevo."); }
    setBusy(false);
  };
  return (
    <footer className="mt-24 bg-[color:var(--brand-primary)] text-[color:var(--brand-bg)]">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 py-16 grid grid-cols-1 md:grid-cols-4 gap-12">
        <div className="md:col-span-2">
          <img src="/brand/logo.png" alt="Mimes Gourmet" className="h-16 w-auto brightness-0 invert" />
          <p className="mt-4 max-w-sm text-sm leading-relaxed text-[color:var(--brand-bg)]/75">
            Mermeladas artesanas en Graus, Huesca. Ingredientes naturales y sabores del Pirineo.
          </p>
          <form onSubmit={submit} className="mt-6 max-w-sm space-y-2">
            <div className="flex gap-2">
              <Input data-testid="footer-newsletter-email" required type="email" placeholder="Tu email" value={email} onChange={(e) => setEmail(e.target.value)}
                className="bg-white/10 border-white/20 text-white placeholder:text-white/50" />
              <Button data-testid="footer-newsletter-submit" disabled={busy} className="bg-[color:var(--brand-terracotta)] hover:bg-[color:var(--brand-terracotta)]/90 text-white shrink-0">Suscribir</Button>
            </div>
            <label className="flex items-start gap-2 cursor-pointer select-none">
              <input type="checkbox" data-testid="footer-newsletter-gdpr" required checked={gdprOk} onChange={(e) => setGdprOk(e.target.checked)}
                className="mt-1 w-3.5 h-3.5 accent-[color:var(--brand-terracotta)]" />
              <span className="text-[11px] leading-relaxed text-[color:var(--brand-bg)]/70">
                Acepto recibir novedades y la <Link to="/privacidad" className="underline hover:text-[color:var(--brand-terracotta)]">política de privacidad</Link>. Puedo darme de baja en cualquier momento.
              </span>
            </label>
          </form>
          <div className="mt-6 flex gap-3">
            <a href="https://instagram.com/mimesgourmet" target="_blank" rel="noreferrer" data-testid="footer-instagram" className="p-2 rounded-full border border-white/20 hover:bg-white/10 transition" aria-label="Instagram">
              <Instagram className="w-4 h-4" strokeWidth={1.5} />
            </a>
            <a href="https://facebook.com/mimesgourmet" target="_blank" rel="noreferrer" data-testid="footer-facebook" className="p-2 rounded-full border border-white/20 hover:bg-white/10 transition" aria-label="Facebook">
              <Facebook className="w-4 h-4" strokeWidth={1.5} />
            </a>
            <a href="mailto:info@mimesgourmet.es" data-testid="footer-email" className="p-2 rounded-full border border-white/20 hover:bg-white/10 transition" aria-label="Email">
              <Mail className="w-4 h-4" strokeWidth={1.5} />
            </a>
          </div>
        </div>
        <div>
          <h4 className="eyebrow text-[color:var(--brand-bg)]/70 mb-4">Contacto</h4>
          <ul className="space-y-3 text-sm text-[color:var(--brand-bg)]/85">
            <li className="flex items-start gap-2">
              <Phone className="w-3.5 h-3.5 mt-1 shrink-0 text-[color:var(--brand-terracotta)]" />
              <a href="tel:+34655958586" className="hover:text-[color:var(--brand-terracotta)]">+34 655 958 586</a>
            </li>
            <li className="flex items-start gap-2">
              <Mail className="w-3.5 h-3.5 mt-1 shrink-0 text-[color:var(--brand-terracotta)]" />
              <a href="mailto:info@mimesgourmet.es" className="hover:text-[color:var(--brand-terracotta)] break-all">info@mimesgourmet.es</a>
            </li>
            <li className="flex items-start gap-2">
              <MapPin className="w-3.5 h-3.5 mt-1 shrink-0 text-[color:var(--brand-terracotta)]" />
              <span>Calle Fray Diego Cera, 21<br />22430 Graus (Huesca)</span>
            </li>
            <li className="flex items-start gap-2">
              <Clock className="w-3.5 h-3.5 mt-1 shrink-0 text-[color:var(--brand-terracotta)]" />
              <span>L-V · 9:00 – 18:00</span>
            </li>
          </ul>
        </div>
        <div>
          <h4 className="eyebrow text-[color:var(--brand-bg)]/70 mb-4">Navega</h4>
          <ul className="space-y-2 text-sm">
            <li><Link to="/tienda" className="hover:text-[color:var(--brand-terracotta)]">Tienda</Link></li>
            <li><Link to="/blog" className="hover:text-[color:var(--brand-terracotta)]">Blog</Link></li>
            <li><Link to="/historia" className="hover:text-[color:var(--brand-terracotta)]">Historia</Link></li>
            <li><Link to="/contacto" className="hover:text-[color:var(--brand-terracotta)]">Contacto</Link></li>
            <li><Link to="/favoritos" className="hover:text-[color:var(--brand-terracotta)]">Favoritos</Link></li>
          </ul>
          <h4 className="eyebrow text-[color:var(--brand-bg)]/70 mb-3 mt-6">Legal</h4>
          <ul className="space-y-2 text-sm">
            <li><Link to="/aviso-legal" className="hover:text-[color:var(--brand-terracotta)]">Aviso legal</Link></li>
            <li><Link to="/privacidad" className="hover:text-[color:var(--brand-terracotta)]">Privacidad</Link></li>
            <li><Link to="/cookies" className="hover:text-[color:var(--brand-terracotta)]">Cookies</Link></li>
            <li><Link to="/condiciones" className="hover:text-[color:var(--brand-terracotta)]">Condiciones</Link></li>
            <li><Link to="/envios" className="hover:text-[color:var(--brand-terracotta)]">Envíos y devoluciones</Link></li>
            <li>
              <button type="button" onClick={openCookiePreferences} data-testid="footer-manage-cookies" className="hover:text-[color:var(--brand-terracotta)] text-left">
                Gestionar cookies
              </button>
            </li>
          </ul>
        </div>
      </div>
      <div className="border-t border-white/10">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-6 text-xs text-[color:var(--brand-bg)]/60 flex justify-between flex-wrap gap-2">
          <span>© {new Date().getFullYear()} Mimes Gourmet · Graus, Huesca</span>
          <span>Elaborado en España</span>
        </div>
      </div>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
        "@context": "https://schema.org", "@type": "Organization", name: "Mimes Gourmet",
        url: "https://mimesgourmet.es",
        logo: "https://mimesgourmet.es/brand/logo.png",
        telephone: "+34655958586",
        email: "info@mimesgourmet.es",
        sameAs: ["https://instagram.com/mimesgourmet", "https://facebook.com/mimesgourmet"],
        address: { "@type": "PostalAddress", streetAddress: "Calle Fray Diego Cera, 21", postalCode: "22430", addressLocality: "Graus", addressRegion: "Huesca", addressCountry: "ES" },
      }) }} />
    </footer>
  );
}
